import React, { useEffect, useState, useRef } from "react";
import NotifyMeModal from "./NotifyMeModal";
import { PRELAUNCH_MODE } from "./PreLaunchBanner";

const SESSION_KEY = "psl_exit_intent_shown";
const SUBSCRIBED_KEY = "psl_launch_subscribed";
const ELIGIBLE_AFTER_MS = 8000; // don't ambush — let them browse first
const MOBILE_SCROLL_THRESHOLD = 600; // px scrolled down before mobile trigger arms

/**
 * Exit-intent trigger that opens NotifyMeModal when the user signals leaving.
 * Desktop: cursor exits the viewport from the top edge.
 * Mobile: rapid scroll-up after scrolling down past threshold (proxy for "leaving").
 * Fires at most once per browser session, never if they've already subscribed,
 * and only when REACT_APP_PRELAUNCH_MODE is on.
 */
export default function ExitIntent() {
  const [open, setOpen] = useState(false);
  const armedAt = useRef(null);
  const lastScrollY = useRef(0);
  const maxScrollY = useRef(0);

  useEffect(() => {
    if (!PRELAUNCH_MODE) return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return;

    armedAt.current = Date.now();

    const fire = () => {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
      if (localStorage.getItem(SUBSCRIBED_KEY) === "1") return;
      if (Date.now() - armedAt.current < ELIGIBLE_AFTER_MS) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    };

    const onMouseOut = (e) => {
      // Desktop: cursor leaves through the top edge with no relatedTarget (= outside viewport)
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY > 10) return;
      fire();
    };

    const onScroll = () => {
      const y = window.scrollY;
      if (y > maxScrollY.current) maxScrollY.current = y;
      // Mobile proxy: scrolled down past threshold, then scrolled up by 200px+
      if (
        maxScrollY.current > MOBILE_SCROLL_THRESHOLD &&
        lastScrollY.current - y > 200 &&
        window.matchMedia("(max-width: 768px)").matches
      ) {
        fire();
      }
      lastScrollY.current = y;
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const onClose = () => {
    setOpen(false);
  };

  const onSubscribed = () => {
    localStorage.setItem(SUBSCRIBED_KEY, "1");
  };

  if (!PRELAUNCH_MODE) return null;

  return (
    <NotifyMeModal
      open={open}
      onClose={onClose}
      source="exit-intent"
      onSubscribed={onSubscribed}
    />
  );
}
